import axiosV2Instance from './axiosV2Instance';

// ========== Request Types ==========

export interface ActivityAllocationItemV2 {
  /** Распределение на активность смены (если не задано earningId). */
  activityId: number;
  amount: number;
  /** Распределение на начисление (Earnings); приоритет над activityId. */
  earningId?: number;
}

export interface ContractAllocationItemV2 {
  contractId: number;
  amount: number;
}

export interface PaymentAllocationV2Request {
  activityAllocations: ActivityAllocationItemV2[];
  contractAllocations: ContractAllocationItemV2[];
}

export interface AutoAllocateV2Request {
  userId?: number;
  siteExecutorId?: number;
  amount: number;
}

// ========== Batch Operations Types ==========

/**
 * Элемент пакетного распределения - один платеж с его распределениями
 */
export interface BatchPaymentAllocationItemV2 {
  paymentId: number;
  activityAllocations: ActivityAllocationItemV2[];
  contractAllocations: ContractAllocationItemV2[];
}

/**
 * Запрос на пакетное распределение множества платежей
 */
export interface BatchPaymentAllocationV2Request {
  allocations: BatchPaymentAllocationItemV2[];
}

/**
 * Результат распределения одного платежа в пакете
 */
export interface BatchPaymentAllocationResultV2 {
  paymentId: number;
  success: boolean;
  allocatedAmount: number;
  remainingAmount: number;
  errorMessage?: string;
}

/**
 * Ответ на пакетное распределение
 */
export interface BatchPaymentAllocationResponseV2 {
  success: boolean;
  totalAllocated: number;
  results: BatchPaymentAllocationResultV2[];
  failedCount: number;
  successCount: number;
}

/**
 * Запрос на создание платежа с немедленным распределением
 */
export interface CreateAndAllocatePaymentV2Request {
  userId: number;
  amount: number;
  date: string;
  comment?: string;
  activityAllocations: ActivityAllocationItemV2[];
  contractAllocations: ContractAllocationItemV2[];
}

/**
 * Ответ на создание платежа с распределением
 */
export interface CreateAndAllocatePaymentV2Response {
  success: boolean;
  paymentId: number;
  allocatedAmount: number;
  remainingAmount: number;
  payment: PaymentAllocationPaymentInfoV2;
}

// ========== Response Types ==========

export interface PaymentAllocationPaymentInfoV2 {
  id: number;
  userId?: number;
  siteExecutorId?: number;
  userName: string;
  /** Пул по открытым строкам; с allocated + remaining */
  amount: number;
  /** Заголовок Payments в БД / журнал (если отличается от amount) */
  documentAmount?: number;
  allocatedAmount: number;
  remainingAmount: number;
  date?: string;
  adminSignature: boolean;
  userSignature: boolean;
  isFullyAllocated: boolean;
}

export interface PaymentAllocationLineInfoV2 {
  id: number;
  type: string;
  description: string;
  amount: number;
  isAllocated: boolean;
  dateCreated: string;
}

export interface PaymentAllocationEntryV2 {
  allocationId: number;
  paymentLineId: number;
  paymentId?: number;  // ID платежа из PaymentLines
  paymentLineType?: string;
  paymentLineDescription?: string;
  paymentLineAmount: number;
  allocationAmount: number;
  allocationDate: string;
  allocationType: string;
  sessionId?: number;
  sessionActivityId?: number;
  contractId?: number;
  sessionUserName?: string;
  sessionSiteName?: string;
  sessionActivityTypeOfWorkName?: string; // Название вида работы активности
  sessionTimeStart?: string;
  sessionTimeEnd?: string;
  sessionHours?: number;
  contractTitle?: string;
  contractDescription?: string;
  contractFixedAmount?: number;
}

export interface PaymentAllocationDetailsV2 {
  payment: PaymentAllocationPaymentInfoV2;
  paymentLines: PaymentAllocationLineInfoV2[];
  /** Распределения по активностям/сессиям (с бэкенда приходит activityAllocations) */
  activityAllocations?: PaymentAllocationEntryV2[];
  /** @deprecated Использовать activityAllocations */
  sessionAllocations?: PaymentAllocationEntryV2[];
  contractAllocations: PaymentAllocationEntryV2[];
  totalAllocatedAmount: number;
  remainingAmount: number;
  totalAllocations: number;
}

export interface UnpaidActivityV2 {
  activityId: number;
  sessionId: number;
  typeOfWorkId: number;
  typeOfWorkName: string;
  timeStart: string;
  timeEnd?: string;
  hours: number;
  rate: number;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
}

export interface UnpaidSessionV2 {
  sessionId: number;
  userId: number;
  userName: string;
  siteId: number;
  siteName: string;
  typeOfWorkId: number;
  typeOfWorkName: string;
  timeStart: string;
  timeEnd?: string;
  hours: number;
  rate: number;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  activities: UnpaidActivityV2[];
}

export interface UnpaidContractV2 {
  contractId: number;
  title: string;
  description: string;
  userId: number;
  userName: string;
  siteId: number;
  siteName: string;
  typeOfWorkId: number;
  typeOfWorkName: string;
  fixedAmount: number;
  paidAmount: number;
  remainingAmount: number;
  dateCreated: string;
}

export interface PaymentAllocationResponseV2 {
  success: boolean;
  message: string;
  paymentId: number;
  allocatedAmount: number;
  remainingAmount: number;
  isFullyAllocated: boolean;
  payment: PaymentAllocationPaymentInfoV2;
  unpaidSessions: UnpaidSessionV2[];
  unpaidContracts: UnpaidContractV2[];
  createdLines: PaymentAllocationLineInfoV2[];
}

export interface AutoAllocateResponseV2 {
  success: boolean;
  message: string;
  paymentId: number;
  allocatedSessions: number;
  allocatedAmount: number;
  remainingAmount: number;
  payment: PaymentAllocationPaymentInfoV2;
}

export interface AllocationDetailsV2 {
  payment: PaymentAllocationPaymentInfoV2;
  unpaidSessions: UnpaidSessionV2[];
  unpaidContracts: UnpaidContractV2[];
}

export interface UnpaidActivitiesByUserV2 {
  userId: number;
  userName: string;
  totalUnpaidAmount: number;
  totalUnpaidActivitiesCount: number;
  sessions: UnpaidSessionV2[];
}

export interface UnpaidActivitiesByUsersResponseV2 {
  siteId: number;
  siteName: string;
  users: UnpaidActivitiesByUserV2[];
}

// ========== API Client ==========

class SitePaymentAllocationV2Api {
  /**
   * Получить распределения платежа
   */
  async getPaymentAllocations(siteId: number, paymentId: number): Promise<PaymentAllocationDetailsV2> {
    const response = await axiosV2Instance.get<PaymentAllocationDetailsV2>(
      `/sites/${siteId}/payment-allocations/payment/${paymentId}`
    );
    return response.data;
  }

  /**
   * Получить распределения для нескольких платежей одним batch запросом (оптимизированная загрузка)
   * Загружает все распределения одним запросом для лучшей производительности
   */
  async getBatchPaymentAllocations(
    siteId: number,
    paymentIds: number[]
  ): Promise<Map<number, PaymentAllocationDetailsV2>> {
    if (paymentIds.length === 0) {
      return new Map();
    }

    try {
      // Используем batch endpoint для загрузки всех распределений одним запросом
      const response = await axiosV2Instance.post<{ [key: number]: PaymentAllocationDetailsV2 }>(
        `/sites/${siteId}/payment-allocations/batch`,
        { paymentIds }
      );

      // Преобразуем объект в Map
      const allocationsMap = new Map<number, PaymentAllocationDetailsV2>();
      Object.entries(response.data).forEach(([paymentIdStr, allocation]) => {
        const paymentId = parseInt(paymentIdStr, 10);
        allocationsMap.set(paymentId, allocation);
      });

      return allocationsMap;
    } catch (error: any) {
      // Fallback на параллельную загрузку, если batch endpoint недоступен
      console.warn('Batch endpoint недоступен, используем параллельную загрузку:', error);
      
      const promises = paymentIds.map(paymentId =>
        this.getPaymentAllocations(siteId, paymentId)
          .then(allocation => ({ paymentId, allocation }))
          .catch(err => {
            console.error(`Error loading allocation for payment ${paymentId}:`, err);
            return null;
          })
      );

      const results = await Promise.all(promises);
      const allocationsMap = new Map<number, PaymentAllocationDetailsV2>();

      results.forEach(result => {
        if (result) {
          allocationsMap.set(result.paymentId, result.allocation);
        }
      });

      return allocationsMap;
    }
  }

  /**
   * Распределить платеж по сессиям, контрактам и статьям
   */
  async allocatePayment(
    siteId: number,
    paymentId: number,
    request: PaymentAllocationV2Request
  ): Promise<PaymentAllocationResponseV2> {
    const response = await axiosV2Instance.post<PaymentAllocationResponseV2>(
      `/sites/${siteId}/payment-allocations/allocate/${paymentId}`,
      request
    );
    return response.data;
  }

  /**
   * Автоматически распределить платеж по неоплаченным сессиям
   */
  async autoAllocatePayment(
    siteId: number,
    paymentId: number,
    request: AutoAllocateV2Request
  ): Promise<AutoAllocateResponseV2> {
    const response = await axiosV2Instance.post<AutoAllocateResponseV2>(
      `/sites/${siteId}/payment-allocations/auto-allocate/${paymentId}`,
      request
    );
    return response.data;
  }

  /**
   * Получить неоплаченные сессии пользователя на объекте
   */
  async getUnpaidSessions(siteId: number, userId: number): Promise<UnpaidSessionV2[]> {
    const response = await axiosV2Instance.get<UnpaidSessionV2[]>(
      `/sites/${siteId}/payment-allocations/unpaid-sessions/${userId}`
    );
    return response.data;
  }

  /**
   * Получить неоплаченные контракты пользователя на объекте
   */
  async getUnpaidContracts(siteId: number, userId: number): Promise<UnpaidContractV2[]> {
    const response = await axiosV2Instance.get<UnpaidContractV2[]>(
      `/sites/${siteId}/payment-allocations/unpaid-contracts/${userId}`
    );
    return response.data;
  }

  /**
   * Получить детали для распределения платежа
   */
  async getAllocationDetails(siteId: number, paymentId: number): Promise<AllocationDetailsV2> {
    const response = await axiosV2Instance.get<AllocationDetailsV2>(
      `/sites/${siteId}/payment-allocations/allocation-details/${paymentId}`
    );
    return response.data;
  }

  /**
   * Получить распределения для активности сессии
   */
  async getActivityAllocations(siteId: number, sessionActivityId: number): Promise<PaymentAllocationEntryV2[]> {
    const response = await axiosV2Instance.get<PaymentAllocationEntryV2[]>(
      `/sites/${siteId}/payment-allocations/activity/${sessionActivityId}/allocations`
    );
    return response.data;
  }

  // ========== Batch Operations ==========

  /**
   * Пакетное распределение множества платежей за один запрос
   * Оптимизировано для множественной оплаты смен
   */
  async batchAllocatePayments(
    siteId: number,
    request: BatchPaymentAllocationV2Request
  ): Promise<BatchPaymentAllocationResponseV2> {
    const response = await axiosV2Instance.post<BatchPaymentAllocationResponseV2>(
      `/sites/${siteId}/payment-allocations/batch-allocate`,
      request
    );
    return response.data;
  }

  /**
   * Создать платеж и сразу распределить его средства
   * Оптимизировано для создания нового платежа с немедленным распределением
   */
  async createAndAllocatePayment(
    siteId: number,
    request: CreateAndAllocatePaymentV2Request
  ): Promise<CreateAndAllocatePaymentV2Response> {
    const response = await axiosV2Instance.post<CreateAndAllocatePaymentV2Response>(
      `/sites/${siteId}/payment-allocations/create-and-allocate`,
      request
    );
    return response.data;
  }

  /**
   * Получить неоплаченные активности для всех сотрудников объекта, сгруппированные по сменам
   */
  async getUnpaidActivitiesByUsers(siteId: number): Promise<UnpaidActivitiesByUsersResponseV2> {
    const response = await axiosV2Instance.get<UnpaidActivitiesByUsersResponseV2>(
      `/sites/${siteId}/payment-allocations/unpaid-activities-by-users`
    );
    return response.data;
  }

  // ========== Bonus (премия) по выбранным сменам ==========

  /**
   * Превью премии по выбранным сменам: часы по типам работ, выплаченная зарплата, средний час
   */
  async getBonusPreview(siteId: number, request: BonusPreviewRequestV2): Promise<BonusPreviewResponseV2> {
    const response = await axiosV2Instance.post<BonusPreviewResponseV2>(
      `/sites/${siteId}/payment-allocations/bonus-preview`,
      request
    );
    return response.data;
  }

  /**
   * Read-only preview плана распределения оплаты смен (фаза C2).
   */
  async previewShiftPayment(
    siteId: number,
    request: ShiftPaymentAllocationRequestV2
  ): Promise<ShiftPaymentAllocationPreviewResponseV2> {
    const response = await axiosV2Instance.post<ShiftPaymentAllocationPreviewResponseV2>(
      `/sites/${siteId}/payment-allocations/allocate-shift-payment/preview`,
      request
    );
    return response.data;
  }

  /**
   * Оплата смен: зарплата по активностям + опционально оплата начисленных премий (Earnings).
   * Idempotency-Key (фаза C3) предотвращает дубли при повторе запроса.
   */
  async allocateShiftPayment(
    siteId: number,
    request: ShiftPaymentAllocationRequestV2,
    idempotencyKey?: string
  ): Promise<ShiftPaymentAllocationResponseV2> {
    const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined;
    const response = await axiosV2Instance.post<ShiftPaymentAllocationResponseV2>(
      `/sites/${siteId}/payment-allocations/allocate-shift-payment`,
      request,
      { headers }
    );
    return response.data;
  }

  /**
   * Начислить премию по ставке ₽/ч по видам работ (без платежей).
   */
  async createHourlyBonusEarnings(
    siteId: number,
    request: CreateHourlyBonusEarningsRequestV2
  ): Promise<CreateHourlyBonusEarningsResponseV2> {
    const response = await axiosV2Instance.post<CreateHourlyBonusEarningsResponseV2>(
      `/sites/${siteId}/payment-allocations/hourly-bonus-earnings`,
      request
    );
    return response.data;
  }

  /**
   * Начисления с остатком к оплате (распределение платежа на earningId).
   */
  async getUnpaidEarningsForAllocation(
    siteId: number,
    params: { userId?: number; siteExecutorId?: number }
  ): Promise<UnpaidEarningForAllocationV2[]> {
    const response = await axiosV2Instance.get<UnpaidEarningForAllocationV2[]>(
      `/sites/${siteId}/payment-allocations/unpaid-earnings-for-allocation`,
      { params }
    );
    return response.data;
  }
}

// ========== Shift payment allocation (вся логика на бэке) ==========

export interface ShiftPaymentAllocationRequestV2 {
  userId?: number;
  siteExecutorId?: number;
  sessionIds: number[];
  selectedPaymentIds: number[];
  newPaymentAmount: number;
  /** Сервер сам вычисляет top-up (кнопка «ВСЁ»). */
  createNewPaymentForRemaining?: boolean;
  newPaymentComment?: string;
  payUnpaidEarnings?: boolean;
  selectedEarningIds?: number[];
}

export interface ShiftPaymentAllocationPreviewResponseV2 {
  success: boolean;
  errorMessage?: string;
  recommendedSalaryAmount: number;
  unpaidBonusAmount: number;
  targetAmount: number;
  selectedPaymentsFunding: number;
  recommendedTopUp: number;
  computedNewPaymentAmount: number;
  newPaymentHeaderAmount: number;
  plannedSalaryAllocated: number;
  plannedBonusAllocated: number;
  effectiveUnallocatedAmount: number;
  activityAllocations: { activityId: number; amount: number }[];
  paymentUsages: { paymentId: number; allocatedAmount: number }[];
}

export interface ShiftPaymentAllocationResponseV2 {
  success: boolean;
  message: string;
  errorMessage?: string;
  salaryAllocated: number;
  bonusAllocated: number;
  newPaymentId?: number | null;
}

// ========== Bonus types ==========

export interface BonusPreviewRequestV2 {
  sessionIds: number[];
}

export interface BonusPreviewByTypeOfWorkV2 {
  typeOfWorkId: number;
  typeOfWorkName: string;
  hours: number;
  paidSalary: number;
  /** Уже выплаченные премии по этому виду работ */
  paidBonus?: number;
  /** Средний час только по зарплате */
  averageHourly: number;
  /** Средний час по ЗП и выплаченным премиям */
  averageHourlySalaryPlusPaidBonus?: number;
}

export interface BonusPreviewActivityV2 {
  activityId: number;
  sessionId: number;
  typeOfWorkId: number;
  typeOfWorkName: string;
  hours: number;
  paidSalary: number;
}

export interface BonusPreviewResponseV2 {
  userId?: number;
  siteExecutorId?: number;
  userName: string;
  totalHours: number;
  totalPaidSalary: number;
  /** Уже выплаченные премии по выбранным активностям */
  totalPaidBonus?: number;
  averageHourlyFromSalary: number;
  /** Средний час по ЗП и уже выплаченным премиям */
  averageHourlySalaryPlusPaidBonus?: number;
  byTypeOfWork: BonusPreviewByTypeOfWorkV2[];
  activities: BonusPreviewActivityV2[];
}

export interface HourlyBonusRateItemV2 {
  typeOfWorkId: number;
  bonusPerHour: number;
}

export interface CreateHourlyBonusEarningsRequestV2 {
  sessionIds: number[];
  userId?: number;
  siteExecutorId?: number;
  ratesByTypeOfWork: HourlyBonusRateItemV2[];
}

export interface CreateHourlyBonusEarningsResponseV2 {
  success: boolean;
  message?: string;
  createdCount: number;
  totalAmount: number;
  createdEarningIds: number[];
}

export interface UnpaidEarningForAllocationV2 {
  id: number;
  amount: number;
  paidToward: number;
  remaining: number;
  earningTypeId: number;
  sourceTypeId: number;
  sourceId: number;
  sessionId: number;
  description: string;
  earningTypeName: string;
  createdAt: string;
}

/** HTTP 409 — платёж изменился параллельной операцией (B3 RowVersion). */
export interface PaymentAllocationConflictV2 {
  paymentId: number;
  rowVersion: number;
  remainingAmount: number;
  message: string;
}

export function getPaymentAllocationConflictMessage(error: unknown): string | null {
  const status = (error as { response?: { status?: number; data?: PaymentAllocationConflictV2 } })?.response?.status;
  if (status !== 409) return null;
  const data = (error as { response?: { data?: PaymentAllocationConflictV2 } }).response?.data;
  return data?.message || 'Платёж изменился другой операцией. Обновите данные и повторите.';
}

export const sitePaymentAllocationV2Api = new SitePaymentAllocationV2Api();

